import { cloneDeep } from 'lodash';
import { type IComponent, ComponentType } from '../types';
import {
  searchJmesPath,
  applyJsonLogic,
  resolveScopedRootVars,
} from '../../utils';

const PARSABLE_OBJECTS = ['x-component-props', 'x-decorator-props'];
const ROOT_PARSABLE_OBJECTS = [
  'title',
  'name',
  'description',
  'x-display',
  'x-pattern',
  'default',
];
const DEFAULT_SKIP_PARSING_COMPONENTS: ComponentType[] = [
  ComponentType.Loop,
  ComponentType.LoadableData,
  ComponentType.Tabs,
  ComponentType.Collapse,
];

export interface IParseJSONSchemaOptions {
  showFallbackKey?: boolean;
  actor?: any;
  skipParsingComponents?: ComponentType[];
  enableParsingComponentNames?: string[];
  t?: (key: string) => string;
}

export enum ComponentConfigType {
  Value = 'value',
  Variable = 'variable',
  JsonLogic = 'json-logic',
  Name = 'name',
  Translate = 'translate',
}

export interface IComponentConfig {
  type: ComponentConfigType;
  conditions?: IJsonLogicField[];
}

export interface IJsonLogicField {
  value: string | number | boolean | string[] | number[];
  valueType: 'value' | 'variable';
  condition: any;
}

const buildLocalOptions = (
  options: IParseJSONSchemaOptions
): IParseJSONSchemaOptions => {
  const localOptions = { ...options };
  if (!localOptions.skipParsingComponents) {
    localOptions.skipParsingComponents = DEFAULT_SKIP_PARSING_COMPONENTS;
  }
  return localOptions;
};

/**
 * @param schema - The schema to parse and replace variables, json-logic, etc.
 * @param data - The data to use for parsing
 * @param options - The options to use for parsing
 * @returns The parsed schema with variables, json-logic, etc. replaced
 */
export const parseJSONSchema = (
  schema: IComponent<any>,
  data: Object,
  options: IParseJSONSchemaOptions = {
    skipParsingComponents: DEFAULT_SKIP_PARSING_COMPONENTS,
    enableParsingComponentNames: [],
  }
): IComponent<any> => {
  const result = cloneDeep(schema);

  const localOptions = buildLocalOptions(options);
  // Check if the component should be skipped based on component type
  const shouldSkipParsing = localOptions.skipParsingComponents?.includes(
    schema['x-component'] as ComponentType
  );
  // Check if the component should be enabled based on component name override component type skipping
  const shouldEnableParsing =
    localOptions.enableParsingComponentNames?.includes(
      schema['name'] as string
    );

  if (shouldSkipParsing && !shouldEnableParsing) {
    return result;
  }

  // Process the root schema if it has parsable objects
  for (const object of PARSABLE_OBJECTS) {
    const objectKey = object as keyof IComponent<any>;
    const configKey = `${objectKey}-config` as keyof IComponent<any>;

    if (result[objectKey] && result[configKey]) {
      (result as any)[objectKey] = parseComponentProps({
        componentProps: result[objectKey],
        configProps: result[configKey],
        data,
        options: localOptions,
      });
    }
  }
  // Process the root items if it has parsable items
  for (const object of ROOT_PARSABLE_OBJECTS) {
    const objectKey = object as keyof IComponent<any>;
    const configKey = 'config' as keyof IComponent<any>;
    if (
      result[objectKey] &&
      result[configKey] &&
      result[configKey][objectKey]
    ) {
      (result as any)[objectKey] = parseTemplateProps({
        componentPropKey: objectKey,
        componentPropValue: result[objectKey],
        data,
        config: result[configKey][objectKey],
        options: localOptions,
      });
    }
  }

  // Recursively process properties
  if (result.properties && typeof result.properties === 'object') {
    for (const [key, property] of Object.entries(result.properties)) {
      if (property && typeof property === 'object') {
        result.properties[key] = parseJSONSchema(property, data, localOptions);
      }
    }
  }
  return result;
};

/**
 * @param componentProps - The component props to parse and replace variables, json-logic, etc.
 * @param configProps - The config props to use for parsing
 * @param data - The data to use for parsing
 * @param options - The options to use for parsing
 * @returns The parsed component props
 */
const parseComponentProps = ({
  componentProps,
  configProps,
  data,
  options,
}: {
  componentProps: Object;
  configProps: Object;
  data: Object;
  options: IParseJSONSchemaOptions;
}): Object => {
  const result: any = {};
  const SKIP_PARSING_PROPS = [
    'childSchema',
    'customComponentSchema',
    'canEditRow',
    'canDeleteRow',
    'canDuplicateRow',
    'expandableSchema',
    'rowShow',
    'titleCustomComponentSchema',
    'nodeCardSchema',
    'nodeCardByType',
    'panelSchema',
    'nodeColor',
    'nodeIcon',
    'panelTitle',
  ];

  for (const [componentPropKey, componentPropValue] of Object.entries(
    componentProps
  )) {
    if (SKIP_PARSING_PROPS.includes(componentPropKey)) {
      result[componentPropKey] = componentPropValue;
      continue;
    }

    const config = (configProps as any)[componentPropKey] as IComponentConfig;
    if (typeof componentPropValue !== 'object') {
      const value = parseTemplateProps({
        componentPropKey,
        componentPropValue,
        data,
        config: config || { type: ComponentConfigType.Value },
        options,
      });
      result[componentPropKey] = value;
    } else {
      if (
        typeof componentPropValue === 'object' &&
        componentPropValue !== null &&
        !Array.isArray(componentPropValue)
      ) {
        result[componentPropKey] = parseComponentProps({
          componentProps: componentPropValue,
          configProps: (configProps as any)[componentPropKey] || {},
          data,
          options,
        });
      } else {
        result[componentPropKey] = componentPropValue;
      }
    }
  }

  return result;
};

/**
 * @param componentPropKey - The key of the component prop to parse
 * @param componentPropValue - The value of the component prop to parse
 * @param data - The data to use for parsing
 * @param configProps - The config props to use for parsing
 * @param options - The options to use for parsing
 * @returns The parsed component prop value
 */
export const parseTemplateProps = ({
  componentPropKey,
  componentPropValue,
  data,
  config,
  options,
}: {
  componentPropKey: string;
  componentPropValue: string;
  data: Object;
  config: IComponentConfig;
  options: IParseJSONSchemaOptions;
}): Object => {
  switch (config.type) {
    case ComponentConfigType.Variable:
      return parseVariable({
        selector: componentPropValue,
        data,
        options,
      });
    case ComponentConfigType.JsonLogic:
      return parseJsonLogic({
        componentPropKey,
        componentPropValue,
        data,
        config,
        options,
      });
    case ComponentConfigType.Value:
      if (typeof componentPropValue === 'string') {
        return parseTemplateString(componentPropValue, data, options);
      }
      return componentPropValue;

    case ComponentConfigType.Translate: {
      const raw = options.t?.(componentPropValue) ?? componentPropValue;
      return parseTemplateString(raw, data, options);
    }

    default:
      return componentPropValue;
  }
};

/**
 * @param selector - Selector to use for parsing
 * @param data - The data to use for parsing
 * @param options - The options to use for parsing
 * @returns The parsed component prop value
 */
export const parseVariable = ({
  selector,
  data,
  options,
}: {
  selector: string;
  data: Object;
  options: IParseJSONSchemaOptions;
}): any => {
  const query = selector?.trim()?.replace(/\{\{/g, '')?.replace(/\}\}/g, '');

  const value = searchJmesPath(data, query);

  if (value === null || value === undefined) {
    if (options.showFallbackKey) {
      return query;
    }
    return undefined; // TODO: check this because we return undefined instead of null!!! Reason is because formily treat null as value and undefined as empty value.
  }

  return value;
};

/**
 * @param componentPropKey - The key of the component prop to parse
 * @param componentPropValue - The value of the component prop to parse
 * @param data - The data to use for parsing
 * @param config - The config to use for parsing
 * @param options - The options to use for parsing
 * @returns The parsed component prop value
 */
const parseJsonLogic = ({
  componentPropKey,
  componentPropValue,
  data,
  config,
  options,
}: {
  componentPropKey: string;
  componentPropValue: string;
  data: Object;
  config: IComponentConfig;
  options: IParseJSONSchemaOptions;
}): any => {
  const { conditions } = config;
  if (!conditions || !Array.isArray(conditions)) {
    console.warn(`No conditions found for property: ${componentPropKey}`);
    return componentPropValue;
  }

  // Evaluate conditions in order and return the first matching value
  for (const conditionConfig of conditions) {
    try {
      const { value, valueType, condition } = conditionConfig;
      const conditionReplacedScopedVars = resolveScopedRootVars(
        condition,
        data
      );

      const conditionString = JSON.stringify(
        conditionReplacedScopedVars
      ).replaceAll('$', '');
      const conditionObject = JSON.parse(conditionString);

      // Evaluate the JSON Logic rule
      const isMatch = applyJsonLogic(conditionObject, data);
      if (isMatch) {
        switch (valueType) {
          case ComponentConfigType.Variable:
            return parseVariable({ selector: value as string, data, options });
          case ComponentConfigType.Value:
            if (typeof value === 'string') {
              return parseTemplateString(value as string, data, options);
            }
            return value;
          default:
            return value;
        }
      }
    } catch (error) {
      console.warn(
        `Failed to evaluate condition for ${componentPropKey}:`,
        error
      );
      continue;
    }
  }

  // If no conditions match, return the original template
  if (options.showFallbackKey) {
    return `"${componentPropValue}"`; // Check this problem for formily
  }

  return undefined;
};

/**
 * Parses a template string containing embedded variables in {%...%} syntax.
 * This is Formily-safe (won't be compiled by Formily's expression engine).
 * Use this for runtime parsing inside Formily forms.
 *
 * @param template - The template string to parse (e.g., "{%@.name%} is my name.")
 * @param data - The data object to use for resolving variables
 * @param options - The options to use for parsing
 * @returns The parsed string with variables replaced
 */
export const parseRuntimeTemplateString = (
  template: string,
  data: Object,
  options: IParseJSONSchemaOptions = {}
): string => {
  if (!template || typeof template !== 'string') {
    return template || '';
  }

  const variablePattern = /\{%([^%]+)%\}/g;

  return template.replace(variablePattern, (match, query) => {
    const trimmedQuery = query.trim();

    if (!trimmedQuery) {
      return match; // Return original if empty query
    }
    const isWithFallbackKey = trimmedQuery.startsWith('$!');

    const jmespathQuery = trimmedQuery.replace(/^\$\!/, '');

    try {
      const value = searchJmesPath(data, jmespathQuery);

      if (value === null || value === undefined) {
        if (isWithFallbackKey) {
          return match; // Return the query as fallback
        }
        if (options.showFallbackKey) {
          return trimmedQuery; // Return the query as fallback
        }
        return ''; // Return empty string if not found
      }

      return String(value);
    } catch (error) {
      console.warn(
        `Failed to parse variable "${trimmedQuery}" in template:`,
        error
      );
      if (options.showFallbackKey) {
        return trimmedQuery; // Return the query as fallback on error
      }
      return ''; // Return empty string on error
    }
  });
};

/**
 * Parses a template string containing embedded variables in {{...}} syntax.
 * Replaces each {{variable}} with its resolved value from the data object.
 * Note: This syntax conflicts with Formily expressions - use parseRuntimeTemplateString
 * with {%...%} syntax when parsing inside Formily forms.
 *
 * @param template - The template string to parse (e.g., "/hello/{{$response.id}}")
 * @param data - The data object to use for resolving variables
 * @param options - The options to use for parsing
 * @returns The parsed string with variables replaced (e.g., "/hello/123")
 */
export const parseTemplateString = (
  template: string,
  data: Object,
  options: IParseJSONSchemaOptions = {}
): string => {
  if (!template || typeof template !== 'string') {
    return template || '';
  }

  // Regex to match {{...}} patterns
  const variablePattern = /\{\{([^}]+)\}\}/g;

  return template.replace(variablePattern, (match, query) => {
    const trimmedQuery = query.trim();

    if (!trimmedQuery) {
      return match;
    }

    const formilyPattern =
      /^\$(?:deps|self|form|values|observable|target|field)\b/;
    if (formilyPattern.test(trimmedQuery)) {
      return match;
    }

    const isWithFallbackKey = trimmedQuery.startsWith('$!');

    try {
      const value = searchJmesPath(data as any, trimmedQuery);
      if (value === null || value === undefined) {
        if (isWithFallbackKey) {
          return match;
        }
        if (options.showFallbackKey) {
          return trimmedQuery;
        }
        return '';
      }

      return String(value);
    } catch (error) {
      console.warn(
        `Failed to parse variable "${trimmedQuery}" in template:`,
        error
      );
      if (options.showFallbackKey) {
        return trimmedQuery;
      }
      return '';
    }
  });
};

export type TemplateSyntax = 'default' | 'runtime';

const FULL_TEMPLATE_PATTERN: Record<TemplateSyntax, RegExp> = {
  default: /^\{\{([^}]+)\}\}$/,
  runtime: /^\{%([^%]+)%\}$/,
};

/**
 * Recursively resolves templates in a JSON-like value against `data`.
 * When a string is made up of a single template, the resolved value
 * (e.g. an array from a JMESPath projection like {{query.ids[].id}}) is used
 * as-is, preserving its type. Strings with embedded or partial templates fall
 * back to string interpolation (parseTemplateString / parseRuntimeTemplateString).
 *
 * @param value - The JSON-like value to resolve (object, array, or string)
 * @param data - The data object to use for resolving variables
 * @param syntax - `default` resolves {{...}}. `runtime` resolves {%...%}, which
 * Formily does not compile, so use it for props of Formily fields.
 * @returns The value with all templates resolved
 */
export const resolveTemplateValue = (
  value: any,
  data: Object,
  syntax: TemplateSyntax = 'default'
): any => {
  if (Array.isArray(value)) {
    return value.map(item => resolveTemplateValue(item, data, syntax));
  }

  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, propValue]) => [
        key,
        resolveTemplateValue(propValue, data, syntax),
      ])
    );
  }

  if (typeof value !== 'string') {
    return value;
  }

  const fullMatch = value.trim().match(FULL_TEMPLATE_PATTERN[syntax]);
  if (!fullMatch) {
    return syntax === 'runtime'
      ? parseRuntimeTemplateString(value, data)
      : parseTemplateString(value, data);
  }

  const query = fullMatch[1].trim();
  return searchJmesPath(
    data as any,
    syntax === 'runtime' ? query.replace(/^\$!/, '') : query
  );
};
